import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Loader2, ArrowRight } from 'lucide-react';

import SectionTitle from './SectionTitle';
import Button from './Button';
import SEO from './SEO'; // Import the SEO component
import config from '../utils/config';

const KaryaPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.url}/events`);
        if (!response.ok) throw new Error('Failed to fetch events');
        const data = await response.json();
        setEvents(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (err) {
        setError('कार्य डेटा लोड करने में समस्या आई है।');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hi-IN', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>सभी कार्य</SectionTitle>
        <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
        <p className="text-lg text-gray-600">कार्य लोड हो रहे हैं...</p>
      </div>
    </section>
  );
  if (error) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>सभी कार्य</SectionTitle>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    </section>
  );

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <SEO 
        title="सभी कार्य" 
        description="सीधी सोच सीधी बात संगठन द्वारा किए गए सभी प्रमुख कार्य, प्रोजेक्ट्स और सामाजिक गतिविधियां। हमारे सामुदायिक विकास कार्यों के बारे में जानें।" 
        keywords="कार्य, प्रोजेक्ट्स, सामाजिक गतिविधियां, सामुदायिक विकास, सीधी सोच सीधी बात"
        url="/karya"
      />
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <SectionTitle>सभी कार्य</SectionTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">संगठन द्वारा किए गए सभी प्रमुख कार्य/प्रोजेक्ट्स</p>
        </div>
        {events.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">🏛️</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">कोई कार्य नहीं मिला</h3>
              <p className="text-gray-500">अभी तक कोई कार्य नहीं है।</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {events.map(event => (
              <div key={event.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group">
                {event.image_url && (
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={`${config.url}${event.image_url}`} 
                      alt={event.title} 
                      className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                      onError={e => { e.target.src = 'https://via.placeholder.com/400x300?text=कार्य+छवि'; }}
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">{event.title}</h3>
                  <div className="flex items-center text-gray-600 text-sm mb-2">
                    <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  {event.location && (
                    <div className="flex items-center text-gray-600 text-sm mb-2">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  {event.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{event.description}</p>
                  )}
                  <div className="mt-auto flex justify-end">
                    <Button to={`/karya/${event.id}`} size="sm" rightIcon={ArrowRight} variant="primary" className="mt-2">और पढ़ें</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default KaryaPage; 